import {
  isLitigationActiveWork,
  litigationCalendarYearPeriod,
  litigationLifecycleStage,
  LIFECYCLE_STAGE_LABELS,
  summarizeLitigationManagement,
} from './litigationLedgerRules.js';
import type {
  LitigationCloseMode,
  LitigationEnforcementStatus,
} from './litigationLedgerRules.js';

export {
  CLOSE_MODE_LABELS,
  ENFORCEMENT_STATUS_LABELS,
  isLitigationActiveWork,
  isLitigationInTrial,
  LIFECYCLE_STAGE_LABELS,
} from './litigationLedgerRules.js';

export const LITIGATION_FEATURE_ID = 'legal-litigation-ledger-v1';
export type LitigationDisputeType = 'litigation' | 'arbitration' | 'labor';
export type LitigationPosition = 'plaintiff' | 'defendant' | 'third';
export type LitigationInstance = 'first' | 'second' | 'retrial' | 'enforcement';
export type LitigationPartyRole = 'our' | 'opponent' | 'third' | 'plaintiff';
export type LitigationMaterialCategory = 'complaint' | 'evidence' | 'summons' | 'judgment' | 'enforcement' | 'other';
export type LitigationDeadlineTone = 'none' | 'upcoming' | 'overdue' | 'done';
export type {LitigationCloseMode, LitigationEnforcementStatus, LitigationLifecycleStage} from './litigationLedgerRules.js';

export type LitigationLedgerParticipant = {
  userId: string;
  displayName: string;
  order: number;
};

export type LitigationLedgerAccess = {
  organizationId: string;
  createdBy: string;
  adminUserIds: string[];
  participants: LitigationLedgerParticipant[];
  isAdmin: boolean;
  currentUserId: string;
  canCreate: boolean;
  /** Unit-scoped permissions, issued by the workbook service (not administrator status). */
  editableOwnerUserIds?: string[];
  canViewAll?: boolean;
  canDeleteWorkbook?: boolean;
};

export type LitigationLedgerStats = {
  total: number;
  amount: number;
  closed: number;
  overdue: number;
  upcoming: number;
  filedThisPeriod: number;
  closedThisPeriod: number;
  inTrial: number;
  active: number;
  archived: number;
  periodStart: string;
  periodEnd: string;
};

/** 空间内的一本诉讼台账工作区。 */
export type LitigationWorkbookScope = 'personal' | 'team';

export type LitigationWorkbookSummary = {
  id: string;
  name: string;
  scope: LitigationWorkbookScope;
  organizationId: string;
  folderId: string;
  workbookFileId: string;
  createdBy: string;
  isAdmin: boolean;
  canEdit: boolean;
  editorCount: number;
  updatedAt: string;
};

export type LitigationDeadlineItem = {
  caseId: string;
  internalNo: string;
  title: string;
  ownerName: string;
  nextDeadline: string;
  tone: LitigationDeadlineTone;
};

export type LitigationIngestSuggestion = {
  title: string;
  courtNo: string;
  opponent: string;
  filingDate: string;
  resultSummary: string;
  excerpt: string;
};

export type LitigationParty = {
  id: string;
  role: LitigationPartyRole;
  name: string;
  contact: string;
  /** 原告、被告按审级分成时记在这一行上。 */
  layer?: LitigationPartyLayer;
};

export type LitigationProcedureNode = {
  id: string;
  name: string;
  plannedDate: string;
  completedDate: string;
  note: string;
  keyDeadline: boolean;
  deadlineKind: string;
};

export type LitigationProgressEntry = {
  id: string;
  date: string;
  content: string;
  createdAt: string;
};

export type LitigationMaterial = {
  fileId: string;
  category: LitigationMaterialCategory;
  originalName: string;
  sizeBytes: number;
  mimeType: string;
  uploadedAt: string;
};

export type LitigationLinkedContract = {
  taskId: string;
  projectId: string;
  name: string;
  archived: boolean;
  sourceFileName: string;
};

/** 案号层次：一审、二审、再审、仲裁、劳动仲裁。 */
export type LitigationCourtNumberLayer = 'first' | 'second' | 'retrial' | 'enforcement' | 'arbitration' | 'labor';

export type LitigationCourtNumbers = Record<LitigationCourtNumberLayer, string>;

/** 客户端一行案号：层次 + 用户填写的案号。 */
export type LitigationCourtNumberRow = {
  layer: LitigationCourtNumberLayer;
  value: string;
};

export const LITIGATION_COURT_NUMBER_LAYERS: readonly {
  id: LitigationCourtNumberLayer;
  label: string;
  example: string;
}[] = [
  {id: 'first', label: '一审案号', example: '（2026）京01民初1号'},
  {id: 'second', label: '二审案号', example: '（2026）京01民终1号'},
  {id: 'retrial', label: '再审案号', example: '（2026）京民再1号'},
  {id: 'enforcement', label: '执行案号', example: '（2026）京01执1号'},
  {id: 'arbitration', label: '仲裁案号', example: '（2026）京仲裁字第1号'},
  {id: 'labor', label: '劳动仲裁案号', example: '京劳人仲字〔2026〕第1号'},
];

/** 案号、管辖层次的查询条件。可以只传争议类型，也可以带上审级和执行状态。 */
export type LitigationLayerQuery = LitigationDisputeType | {
  disputeType?: LitigationDisputeType;
  instance?: LitigationInstance | '';
  enforcementStatus?: string;
};

/**
 * 从字符串或案件上下文里取出争议类型。
 * @param input 争议类型或案件上下文
 */
function litigationLayerDisputeType(input: LitigationLayerQuery): LitigationDisputeType {
  if (typeof input === 'string') return input === 'arbitration' || input === 'labor' ? input : 'litigation';
  const dispute = input?.disputeType;
  return dispute === 'arbitration' || dispute === 'labor' ? dispute : 'litigation';
}

/**
 * 案件类型为执行时，案号和管辖不再使用一审、二审。
 * 仲裁和劳动仲裁优先于执行状态。
 * @param input 争议类型或案件上下文
 */
function litigationLayerIsEnforcement(input: LitigationLayerQuery): boolean {
  if (typeof input === 'string' || !input || litigationLayerDisputeType(input) !== 'litigation') return false;
  const instance = String(input.instance || '');
  const status = String(input.enforcementStatus || '').trim().toLowerCase();
  return instance === 'enforcement' || (status !== '' && status !== 'none');
}

/**
 * 案号层次随案件类型变化。诉讼保留一审、二审、再审；执行只保留执行案号。
 * @param input 争议类型，或带审级、执行状态的案件
 */
export function litigationCourtNumberLayersForDispute(input: LitigationLayerQuery): readonly {
  id: LitigationCourtNumberLayer;
  label: string;
  example: string;
}[] {
  const disputeType = litigationLayerDisputeType(input);
  if (disputeType === 'arbitration') return LITIGATION_COURT_NUMBER_LAYERS.filter((layer) => layer.id === 'arbitration');
  if (disputeType === 'labor') return LITIGATION_COURT_NUMBER_LAYERS.filter((layer) => layer.id === 'labor');
  if (litigationLayerIsEnforcement(input)) return LITIGATION_COURT_NUMBER_LAYERS.filter((layer) => layer.id === 'enforcement');
  return LITIGATION_COURT_NUMBER_LAYERS.filter((layer) => layer.id === 'first' || layer.id === 'second' || layer.id === 'retrial');
}

/**
 * 诉讼案件的审级取已填写案号中最高的一层。再审优先于二审，二审优先于一审。
 * @param rows 案号行
 */
export function litigationInstanceFromCourtRows(rows: LitigationCourtNumberRow[]): '' | 'first' | 'second' | 'retrial' {
  const filled = new Set(rows.filter((row) => row.value.trim()).map((row) => row.layer));
  if (filled.has('retrial')) return 'retrial';
  if (filled.has('second')) return 'second';
  if (filled.has('first')) return 'first';
  return '';
}

/**
 * 空的分层案号。
 */
export function emptyLitigationCourtNumbers(): LitigationCourtNumbers {
  return {first: '', second: '', retrial: '', enforcement: '', arbitration: '', labor: ''};
}

/**
 * 规范化分层案号，每层最多 80 字。
 * @param value 原始对象
 */
export function normalizeLitigationCourtNumbers(value: unknown): LitigationCourtNumbers {
  if (Array.isArray(value)) {
    const next = emptyLitigationCourtNumbers();
    for (const row of normalizeLitigationCourtNumberRows(value)) {
      if (row.value) next[row.layer] = row.value;
    }
    return next;
  }
  const record = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  const next = emptyLitigationCourtNumbers();
  for (const layer of LITIGATION_COURT_NUMBER_LAYERS) {
    next[layer.id] = String(record[layer.id] ?? '').trim().slice(0, 80);
  }
  return next;
}

/**
 * 把已填写的案号合成一格，供列表和 Excel 显示。
 * @param numbers 分层案号或按行填写的案号
 */
export function litigationCourtNumberSummary(numbers: LitigationCourtNumbers | LitigationCourtNumberRow[]): string {
  if (Array.isArray(numbers)) return numbers.map((row) => row.value.trim()).filter(Boolean).join('；');
  return LITIGATION_COURT_NUMBER_LAYERS.map((layer) => numbers[layer.id]).filter(Boolean).join('；');
}

/**
 * 把 Excel 里用分号连成一行的案号拆回审级。
 * 第一段是一审，第二段是二审，再往后归入再审。只有一个层次时不拆出二审。
 * @param raw 单元格文本
 * @param disputeType 案件类型
 */
export function splitLitigationCourtNumberCell(raw: string, disputeType: LitigationDisputeType = 'litigation'): LitigationCourtNumberRow[] {
  const ids = litigationCourtNumberLayersForDispute(disputeType).map((layer) => layer.id);
  const first = ids[0] || 'first';
  const parts = String(raw || '').split(/[；;]/).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return [];
  if (ids.length < 2) return [{layer: first, value: parts.join('；').slice(0, 80)}];
  return parts.slice(0, ids.length).map((part, index) => ({
    layer: ids[index],
    value: (index === ids.length - 1 ? parts.slice(index).join('；') : part).slice(0, 80),
  }));
}

/**
 * 判断是不是案号层次。
 * @param value 原始值
 */
export function isLitigationCourtNumberLayer(value: string): value is LitigationCourtNumberLayer {
  return LITIGATION_COURT_NUMBER_LAYERS.some((layer) => layer.id === value);
}

/**
 * 案号行保持各自层次，不按顺序改挂到别的层。
 * @param rows 已有案号行
 * @param _context 案件类型与审级，仅用于调用方对齐参数
 */
export function alignLitigationCourtNumberRows(
  rows: LitigationCourtNumberRow[],
  _context: {disputeType?: LitigationDisputeType; instance?: LitigationInstance | ''},
): LitigationCourtNumberRow[] {
  return rows.map((row) => ({layer: row.layer, value: row.value}));
}

/** 管辖层次：一审法院、二审法院、执行法院、仲裁机构、劳动仲裁机构。 */
export type LitigationVenueLayer = 'first' | 'second' | 'enforcement' | 'arbitration' | 'labor';

/** 一行管辖：层次 + 用户填写的机构名称。 */
export type LitigationVenueRow = {
  layer: LitigationVenueLayer;
  value: string;
};

export const LITIGATION_VENUE_LAYERS: readonly {id: LitigationVenueLayer; label: string}[] = [
  {id: 'first', label: '一审法院'},
  {id: 'second', label: '二审法院'},
  {id: 'enforcement', label: '执行法院'},
  {id: 'arbitration', label: '仲裁机构'},
  {id: 'labor', label: '劳动仲裁机构'},
];

/**
 * 判断是不是管辖层次。
 * @param value 原始值
 */
export function isLitigationVenueLayer(value: string): value is LitigationVenueLayer {
  return LITIGATION_VENUE_LAYERS.some((layer) => layer.id === value);
}

/**
 * 法院层次用法院名录检索，仲裁机构保持手填。
 * @param layer 管辖层次
 */
export function litigationVenueLayerUsesCourtDirectory(layer: string): boolean {
  return layer === 'first' || layer === 'second' || layer === 'enforcement';
}

/**
 * 管辖层次随案件类型变化。诉讼保留一审、二审法院；执行只保留执行法院。
 * @param input 争议类型，或带审级、执行状态的案件
 */
export function litigationVenueLayersForDispute(input: LitigationLayerQuery): readonly {
  id: LitigationVenueLayer;
  label: string;
}[] {
  const disputeType = litigationLayerDisputeType(input);
  if (disputeType === 'arbitration') return LITIGATION_VENUE_LAYERS.filter((layer) => layer.id === 'arbitration');
  if (disputeType === 'labor') return LITIGATION_VENUE_LAYERS.filter((layer) => layer.id === 'labor');
  if (litigationLayerIsEnforcement(input)) return LITIGATION_VENUE_LAYERS.filter((layer) => layer.id === 'enforcement');
  return LITIGATION_VENUE_LAYERS.filter((layer) => layer.id === 'first' || layer.id === 'second');
}

/**
 * 读出按行填写的管辖。旧的单格管辖归入一审法院。
 * @param value 原始管辖
 */
export function normalizeLitigationVenueRows(value: unknown): LitigationVenueRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const record = item && typeof item === 'object' ? item as Record<string, unknown> : {};
    const layer = String(record.layer ?? '');
    if (!isLitigationVenueLayer(layer)) return [];
    return [{layer, value: String(record.value ?? '').trim().slice(0, 80)}];
  }).slice(0, 12);
}

/**
 * 管辖行保持各自层次，不按顺序改挂到别的层。
 * @param rows 已有管辖行
 * @param _context 案件类型与审级，仅用于调用方对齐参数
 */
export function alignLitigationVenueRows(
  rows: LitigationVenueRow[],
  _context: {disputeType?: LitigationDisputeType; instance?: LitigationInstance | ''},
): LitigationVenueRow[] {
  return rows.map((row) => ({layer: row.layer, value: row.value}));
}

/**
 * 把已填写的管辖合成一格，供列表和 Excel 显示。
 * @param rows 按行填写的管辖
 */
export function litigationVenueSummary(rows: LitigationVenueRow[]): string {
  return rows.map((row) => row.value.trim()).filter(Boolean).join('；');
}

/**
 * 把导出格里的分号拆回上下两行。第一段是一审法院，后面是二审法院。
 * 只有一层的案件不拆开。
 * @param raw 单元格文本
 * @param disputeType 案件类型
 */
export function splitLitigationVenueCell(raw: string, disputeType: LitigationDisputeType = 'litigation'): LitigationVenueRow[] {
  const layers = litigationVenueLayersForDispute(disputeType);
  const first = layers[0]?.id || 'first';
  const second = layers[1]?.id;
  const parts = String(raw || '').split(/[；;]/).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return [];
  if (!second) return [{layer: first, value: parts.join('；').slice(0, 80)}];
  if (parts.length === 1) return [{layer: first, value: parts[0].slice(0, 80)}];
  return [
    {layer: first, value: parts[0].slice(0, 80)},
    {layer: second, value: parts.slice(1).join('；').slice(0, 80)},
  ];
}

/**
 * 编辑管辖时按行展示。已经分行的数据保持原样；整段用分号拼在一行时拆回一审、二审。
 * @param item 案件
 */
export function resolveLitigationVenueRows(item: {
  venue?: string;
  venues?: unknown;
  disputeType?: LitigationDisputeType;
  instance?: LitigationInstance | '';
  enforcementStatus?: string;
}): LitigationVenueRow[] {
  const stored = normalizeLitigationVenueRows(item.venues);
  const collapsed = stored.length === 1 && /[；;]/.test(stored[0]?.value || '');
  const rows = stored.length && !collapsed
    ? stored
    : splitLitigationVenueCell(collapsed ? stored[0].value : String(item.venue || ''), item.disputeType || 'litigation');
  const allowed = litigationVenueLayersForDispute(item);
  if (allowed.length >= 2) return rows;
  const fallback = allowed[0]?.id || 'first';
  return rows.map((row) => ({layer: fallback, value: row.value}));
}

/**
 * 读出按行填写的案号。兼容以前按层次各存一格的数据。
 * @param value 原始案号
 */
export function normalizeLitigationCourtNumberRows(value: unknown): LitigationCourtNumberRow[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      const record = item && typeof item === 'object' ? item as Record<string, unknown> : {};
      const layer = String(record.layer ?? '');
      if (!isLitigationCourtNumberLayer(layer)) return [];
      return [{layer, value: String(record.value ?? '').trim().slice(0, 80)}];
    }).slice(0, 12);
  }
  const record = normalizeLitigationCourtNumbers(value);
  return LITIGATION_COURT_NUMBER_LAYERS.flatMap((layer) => (
    record[layer.id] ? [{layer: layer.id, value: record[layer.id]}] : []
  ));
}

export type LitigationCase = {
  /** Newly inserted rows retain blank business choices until explicitly filled. */
  unfilledFields?: string[];
  id: string;
  organizationId: string;
  ownerUserId: string;
  ownerName: string;
  participantOrder: number;
  rowOrder: number;
  internalNo: string;
  /** 所属单位。按填写人自动写入全称、简称或代码，团队成员不能手改。 */
  organizationUnit?: string;
  /** 案件性质。民事或行政，由办案人员在列表中选择。 */
  caseNature?: LitigationCaseNature | '';
  courtNo: string;
  /** 按行填写的案号。每一行是一个层次和用户输入的案号。 */
  courtNumbers?: LitigationCourtNumberRow[] | LitigationCourtNumbers;
  /**
   * 诉讼与执行互相关联的案号。
   * 诉讼或仲裁案件填写进入执行后的执行案号；执行案件填写生效裁判文书案号。
   */
  relatedCourtNo?: string;
  /** 便于识别的案件名称（与标准案由分开存储）。 */
  caseName: string;
  /** 案件背景介绍，由案件名称格右下角打开后填写。 */
  caseBackground?: string;
  title: string;
  disputeType: LitigationDisputeType;
  instance: LitigationInstance;
  position: LitigationPosition;
  ourEntity: string;
  parties: LitigationParty[];
  venue: string;
  /** 按行填写的管辖。每一行是一个层次和用户输入的机构。 */
  venues?: LitigationVenueRow[];
  filingDate: string;
  claimSummary: string;
  /** 被告反诉请求。案件类型为执行时不填写。 */
  counterclaimSummary?: string;
  /**
   * 案涉金额中的本诉、本请求或申请执行标的金额，单位为元。
   * 与「标的额」分开保存。
   */
  principalAmount?: number;
  /** 案涉金额中的反诉或反请求金额，单位为元。执行案件不填写。 */
  counterclaimAmount?: number;
  amount: number;
  currency: string;
  preservation: boolean;
  handler: string;
  counsel: string;
  /** 代理机构（律所）名称。 */
  counselOrg: string;
  resultSummary: string;
  /** 反诉或反请求的判决、裁决结果。执行案件不填写。 */
  counterclaimResult?: string;
  /** 判决/执行中的非货币结果（继续履行、返还财物、赔礼道歉、行为给付等）。 */
  nonMonetaryResult: string;
  /** 判决或裁决支持的本诉、本请求金额，或执行到位金额，单位为元。 */
  awardedAmount?: number;
  /** 反诉或反请求获支持的金额，单位为元。执行案件不填写。 */
  counterclaimAwardedAmount?: number;
  effectiveDate: string;
  closed: boolean;
  closedAt: string;
  closeMode: LitigationCloseMode;
  enforcementStatus: LitigationEnforcementStatus;
  litigationClosedAt: string;
  enforcementClosedAt: string;
  progressEntries: LitigationProgressEntry[];
  nodes: LitigationProcedureNode[];
  materials: LitigationMaterial[];
  linkedContracts: LitigationLinkedContract[];
  createdAt: string;
  updatedAt: string;
};

export const DISPUTE_TYPE_LABELS: Record<LitigationDisputeType, string> = {
  litigation: '诉讼',
  arbitration: '仲裁',
  labor: '劳动仲裁',
};

export const POSITION_LABELS: Record<LitigationPosition, string> = {
  plaintiff: '原告·申请人',
  defendant: '被告·被申请人',
  third: '第三人',
};

export const INSTANCE_LABELS: Record<LitigationInstance, string> = {
  first: '一审',
  second: '二审',
  retrial: '再审',
  enforcement: '执行',
};

export const PARTY_ROLE_LABELS: Record<LitigationPartyRole, string> = {
  our: '我方',
  plaintiff: '原告',
  opponent: '对方',
  third: '第三人',
};

export const MATERIAL_CATEGORY_LABELS: Record<LitigationMaterialCategory, string> = {
  complaint: '起诉状/申请书',
  evidence: '证据',
  summons: '传票/通知',
  judgment: '判决/裁决',
  enforcement: '执行材料',
  other: '其他',
};

const LITIGATION_TEMPLATE: Array<Pick<LitigationProcedureNode, 'id' | 'name' | 'keyDeadline' | 'deadlineKind'>> = [
  {id: 'n_intake', name: '收案评估', keyDeadline: false, deadlineKind: 'custom'},
  {id: 'n_filing', name: '立案受理', keyDeadline: true, deadlineKind: 'custom'},
  {id: 'n_evidence', name: '举证', keyDeadline: true, deadlineKind: 'evidence'},
  {id: 'n_hearing', name: '开庭', keyDeadline: true, deadlineKind: 'hearing'},
  {id: 'n_judgment', name: '裁判/调解', keyDeadline: true, deadlineKind: 'custom'},
  {id: 'n_appeal', name: '上诉', keyDeadline: true, deadlineKind: 'appeal'},
  {id: 'n_second_evidence', name: '二审举证', keyDeadline: true, deadlineKind: 'evidence'},
  {id: 'n_second_hearing', name: '二审开庭', keyDeadline: true, deadlineKind: 'hearing'},
  {id: 'n_second_judgment', name: '二审裁判', keyDeadline: true, deadlineKind: 'custom'},
  {id: 'n_retrial_filing', name: '再审立案', keyDeadline: true, deadlineKind: 'appeal'},
  {id: 'n_retrial_evidence', name: '再审举证', keyDeadline: true, deadlineKind: 'evidence'},
  {id: 'n_retrial_hearing', name: '再审开庭', keyDeadline: true, deadlineKind: 'hearing'},
  {id: 'n_retrial_judgment', name: '再审裁判', keyDeadline: true, deadlineKind: 'custom'},
  {id: 'n_enforcement', name: '执行', keyDeadline: true, deadlineKind: 'enforcement'},
  {id: 'n_close', name: '结案', keyDeadline: false, deadlineKind: 'custom'},
];

const ARBITRATION_TEMPLATE: Array<Pick<LitigationProcedureNode, 'id' | 'name' | 'keyDeadline' | 'deadlineKind'>> = [
  {id: 'n_apply', name: '申请/答辩', keyDeadline: true, deadlineKind: 'custom'},
  {id: 'n_tribunal', name: '组庭', keyDeadline: false, deadlineKind: 'custom'},
  {id: 'n_hearing', name: '开庭', keyDeadline: true, deadlineKind: 'hearing'},
  {id: 'n_award', name: '裁决', keyDeadline: true, deadlineKind: 'custom'},
  {id: 'n_enforcement', name: '执行', keyDeadline: true, deadlineKind: 'enforcement'},
  {id: 'n_close', name: '结案', keyDeadline: false, deadlineKind: 'custom'},
];

const ENFORCEMENT_TEMPLATE: Array<Pick<LitigationProcedureNode, 'id' | 'name' | 'keyDeadline' | 'deadlineKind'>> = [
  {id: 'n_filing', name: '立案受理', keyDeadline: true, deadlineKind: 'custom'},
  {id: 'n_asset_search', name: '财产查控', keyDeadline: true, deadlineKind: 'enforcement'},
  {id: 'n_asset_disposal', name: '财产处置', keyDeadline: true, deadlineKind: 'enforcement'},
  {id: 'n_distribution', name: '案款发放', keyDeadline: true, deadlineKind: 'enforcement'},
  {id: 'n_settlement', name: '执行和解', keyDeadline: false, deadlineKind: 'custom'},
  {id: 'n_close', name: '结案', keyDeadline: false, deadlineKind: 'custom'},
];

/** 只属于执行案件的阶段。选中这些阶段时，程序节点改成执行程序。 */
const ENFORCEMENT_ONLY_STAGE_NAMES = new Set(['财产查控', '财产处置', '案款发放', '执行和解']);

const LABOR_TEMPLATE: Array<Pick<LitigationProcedureNode, 'id' | 'name' | 'keyDeadline' | 'deadlineKind'>> = [
  {id: 'n_apply', name: '申请', keyDeadline: true, deadlineKind: 'custom'},
  {id: 'n_mediate', name: '调解/开庭', keyDeadline: true, deadlineKind: 'hearing'},
  {id: 'n_award', name: '裁决', keyDeadline: true, deadlineKind: 'custom'},
  {id: 'n_sue', name: '起诉（可选）', keyDeadline: true, deadlineKind: 'appeal'},
  {id: 'n_close', name: '结案', keyDeadline: false, deadlineKind: 'custom'},
];

/**
 * 按争议类型生成默认程序节点。
 * @param disputeType 争议类型
 */
export function procedureTemplateFor(disputeType: LitigationDisputeType): LitigationProcedureNode[] {
  const source = disputeType === 'arbitration'
    ? ARBITRATION_TEMPLATE
    : disputeType === 'labor'
      ? LABOR_TEMPLATE
      : LITIGATION_TEMPLATE;
  return source.map((item) => ({
    ...item,
    plannedDate: '',
    completedDate: '',
    note: '',
  }));
}

/**
 * 规范化争议类型。
 * @param value 原始值
 */
export function normalizeDisputeType(value: unknown): LitigationDisputeType {
  return value === 'arbitration' || value === 'labor' ? value : 'litigation';
}

/**
 * 规范化我方地位。
 * @param value 原始值
 */
export function normalizePosition(value: unknown): LitigationPosition {
  return value === 'defendant' || value === 'third' ? value : 'plaintiff';
}

/**
 * 规范化审级。
 * @param value 原始值
 */
export function normalizeInstance(value: unknown): LitigationInstance {
  return value === 'second' || value === 'retrial' || value === 'enforcement' ? value : 'first';
}

/**
 * 当前阶段：审理中、未立案，以及审级为执行且执行中时，显示第一个未完成节点。收案评估和结案不作为进行中的阶段名。
 * @param item 案件
 */
export function currentStageName(item: Pick<LitigationCase, 'closed' | 'nodes' | 'filingDate' | 'createdAt' | 'instance' | 'closedAt' | 'closeMode' | 'enforcementStatus' | 'litigationClosedAt' | 'enforcementClosedAt'>): string {
  const stage = litigationLifecycleStage(item);
  if (stage === 'in_trial' || stage === 'not_filed' || (stage === 'enforcement' && item.instance === 'enforcement')) {
    const pending = item.nodes.find((node) => !node.completedDate && node.name !== '收案评估' && node.name !== '结案')
      ?? item.nodes.find((node) => node.keyDeadline && !node.completedDate)
      ?? item.nodes.find((node) => !node.completedDate);
    return pending?.name || LIFECYCLE_STAGE_LABELS[stage];
  }
  return LIFECYCLE_STAGE_LABELS[stage];
}

/**
 * 下一期限：仅在办案件计算关键节点计划日。
 * @param item 案件
 */
export function nextDeadlineDate(item: Pick<LitigationCase, 'closed' | 'nodes' | 'filingDate' | 'createdAt' | 'instance' | 'closedAt' | 'closeMode' | 'enforcementStatus' | 'litigationClosedAt' | 'enforcementClosedAt'>): string {
  if (!isLitigationActiveWork(item)) return '';
  const dates = item.nodes
    .filter((node) => node.keyDeadline && !node.completedDate && node.plannedDate)
    .map((node) => node.plannedDate)
    .sort();
  return dates[0] ?? '';
}

/**
 * 期限预警色。
 * @param dateValue 计划日
 * @param closed 是否结案
 * @param nowMs 当前时间
 */
export function deadlineTone(dateValue: string, closed: boolean, nowMs = Date.now()): LitigationDeadlineTone {
  if (closed) return 'done';
  if (!dateValue) return 'none';
  const time = Date.parse(`${dateValue}T23:59:59`);
  if (!Number.isFinite(time)) return 'none';
  if (time < nowMs) return 'overdue';
  if (time - nowMs <= 7 * 86400000) return 'upcoming';
  return 'none';
}

/**
 * 汇总调用方可见范围内的案件。调用方必须先完成权限过滤。
 * 新收/结案/在审/在办为平台固定口径，方案只能改列表列，不能改这些定义。
 * @param cases 已按权限和页面条件过滤的案件
 * @param nowMs 当前时间
 */
export function summarizeLitigationCases(cases: LitigationCase[], nowMs = Date.now()): LitigationLedgerStats {
  const now = new Date(nowMs);
  const management = summarizeLitigationManagement(cases, litigationCalendarYearPeriod(now));
  const deadline = cases.reduce((summary, item) => {
    const tone = deadlineTone(nextDeadlineDate(item), !isLitigationActiveWork(item), nowMs);
    if (tone === 'overdue') summary.overdue += 1;
    if (tone === 'upcoming') summary.upcoming += 1;
    return summary;
  }, {overdue: 0, upcoming: 0});
  return {
    ...management,
    closed: management.archived,
    overdue: deadline.overdue,
    upcoming: deadline.upcoming,
  };
}

/**
 * 按日期排序后的进展。无日期的条目排在后面，保持录入顺序。
 * @param entries 进展
 */
export function sortedLitigationProgress(entries: LitigationProgressEntry[] | undefined): LitigationProgressEntry[] {
  return [...(entries ?? [])].sort((left, right) => {
    if (left.date && right.date && left.date !== right.date) return left.date.localeCompare(right.date);
    if (left.date && !right.date) return -1;
    if (!left.date && right.date) return 1;
    return left.createdAt.localeCompare(right.createdAt);
  });
}

/**
 * 台账列表展示的最新一条进展。
 * @param item 案件
 */
export function latestLitigationProgress(item: Pick<LitigationCase, 'progressEntries'>): string {
  const entries = sortedLitigationProgress(item.progressEntries);
  return entries[entries.length - 1]?.content ?? '';
}

/**
 * 列表展示用的主对方名称。
 * @param parties 当事人
 */
export function primaryOpponentName(parties: LitigationParty[]): string {
  return partyNameByRole(parties, 'opponent');
}

/**
 * 某一角色当事人的名称。多名用顿号接上。
 * @param parties 当事人
 * @param role 角色
 */
export function partyNameByRole(parties: LitigationParty[], role: LitigationPartyRole): string {
  return parties.find((item) => item.role === role && item.name.trim())?.name.trim()
    || parties.filter((item) => item.role === role).map((item) => item.name.trim()).filter(Boolean).join('、');
}

/** 原告、被告按审级分成的行。 */
export type LitigationPartyLayer = 'first' | 'second' | 'retrial' | 'enforcement' | 'arbitration' | 'labor';

/** 案件性质。 */
export type LitigationCaseNature = 'civil' | 'administrative';

/**
 * 判断是不是当事人层次。
 * @param value 原始值
 */
/**
 * 从当事人身份文字认出审级。认不出时返回空，不猜到别的行。
 * @param text 身份原文
 * @param disputeType 案件类型
 */
export function litigationPartyLayerFromRoleText(text: string, disputeType?: LitigationDisputeType): LitigationPartyLayer | '' {
  if (text.includes('再审')) return 'retrial';
  if (text.includes('二审')) return 'second';
  if (text.includes('执行')) return 'enforcement';
  if (text.includes('劳动')) return 'labor';
  if (text.includes('仲裁')) return disputeType === 'labor' ? 'labor' : 'arbitration';
  if (text.includes('一审')) return 'first';
  return '';
}

export function isLitigationPartyLayer(value: string): value is LitigationPartyLayer {
  return value === 'first' || value === 'second' || value === 'retrial' || value === 'enforcement' || value === 'arbitration' || value === 'labor';
}

/**
 * 原告侧、被告侧要分成的层次。二审、再审在一审之后加行。
 * @param context 案件类型与审级
 */
export function litigationPartyLayersForCase(context: {disputeType?: LitigationDisputeType; instance?: LitigationInstance | ''}): LitigationPartyLayer[] {
  if (context.disputeType === 'arbitration') return ['arbitration'];
  if (context.disputeType === 'labor') return ['labor'];
  if (context.instance === 'enforcement') return ['enforcement'];
  if (context.instance === 'retrial') return ['first', 'second', 'retrial'];
  if (context.instance === 'second') return ['first', 'second'];
  return ['first'];
}

const PARTY_LAYER_LABELS: Record<'plaintiff' | 'opponent', Record<LitigationPartyLayer, string>> = {
  plaintiff: {first: '原告', second: '上诉人', retrial: '再审申请人', enforcement: '执行申请人', arbitration: '申请人', labor: '申请人'},
  opponent: {first: '被告', second: '被上诉人', retrial: '再审被申请人', enforcement: '被执行人', arbitration: '被申请人', labor: '被申请人'},
};

/**
 * 当事人行左侧名称。
 * @param role 原告侧或被告侧
 * @param layer 层次
 */
export function partyLayerLabel(role: 'plaintiff' | 'opponent', layer: LitigationPartyLayer): string {
  return PARTY_LAYER_LABELS[role][layer];
}

/**
 * 按层次取出某一侧的名称。没有单独记录层次时，按现有当事人顺序填入各行。
 * @param parties 当事人
 * @param role 角色
 * @param layers 要展示的层次
 */
export function partyLayerRows(
  parties: LitigationParty[],
  role: LitigationPartyRole,
  layers: LitigationPartyLayer[],
): Array<{layer: LitigationPartyLayer; name: string}> {
  const named = parties.filter((item) => item.role === role);
  const byLayer = new Map<LitigationPartyLayer, string>();
  named.forEach((item, index) => {
    const layer = item.layer && layers.includes(item.layer) ? item.layer : layers[index];
    if (layer && !byLayer.has(layer)) byLayer.set(layer, item.name.trim());
  });
  return layers.map((layer) => ({layer, name: byLayer.get(layer) || ''}));
}

/**
 * 把某一层次的名称写回当事人。其他角色不动。
 * @param parties 当事人
 * @param role 角色
 * @param layer 层次
 * @param name 名称
 * @param id 新行编号
 * @param layers 当前全部层次
 */
export function writePartyLayerName(
  parties: LitigationParty[],
  role: LitigationPartyRole,
  layer: LitigationPartyLayer,
  name: string,
  id: string,
  layers: LitigationPartyLayer[],
): LitigationParty[] {
  const rows = partyLayerRows(parties, role, layers).map((row) => (
    row.layer === layer ? {...row, name: name.trim().slice(0, 200)} : row
  ));
  const others = parties.filter((item) => item.role !== role);
  const next = rows.filter((row) => row.name).map((row) => ({id: `${id}_${row.layer}`, role, name: row.name, contact: '', layer: row.layer}));
  return [...others, ...next];
}

/**
 * 列表和 Excel 里某一侧当事人的名称。多名仍用顿号接上，不改挂到别的案子。
 * @param parties 当事人
 * @param role 角色
 * @param layers 当前层次
 */
export function formatPartyLayerCell(parties: LitigationParty[], role: LitigationPartyRole, layers: LitigationPartyLayer[]): string {
  if (role !== 'plaintiff' && role !== 'opponent') return partyNameByRole(parties, role);
  return partyLayerRows(parties, role, layers)
    .filter((row) => row.name.trim())
    .map((row) => `${partyLayerLabel(role, row.layer)}：${row.name.trim()}`)
    .join('；');
}

/**
 * 把「被告：王继兰；被上诉人：张三」拆回各层。没有层次标记时返回空，交给原来的单名写入。
 * @param value 单元格原文
 * @param role 角色
 * @param _disputeType 案件类型
 */
export function parsePartyLayerCell(
  value: string,
  role: LitigationPartyRole,
  _disputeType: LitigationDisputeType,
): Array<{layer: LitigationPartyLayer; name: string}> | null {
  if (role !== 'plaintiff' && role !== 'opponent') return null;
  const byLabel = new Map(Object.entries(PARTY_LAYER_LABELS[role]).map(([layer, label]) => [label, layer as LitigationPartyLayer]));
  const parts = value.split(/[；;]/).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return null;
  const parsed: Array<{layer: LitigationPartyLayer; name: string}> = [];
  for (const part of parts) {
    const splitAt = part.includes('：') ? part.indexOf('：') : part.indexOf(':');
    if (splitAt <= 0) return null;
    const layer = byLabel.get(part.slice(0, splitAt).trim());
    if (!layer) return null;
    parsed.push({layer, name: part.slice(splitAt + 1).trim().slice(0, 200)});
  }
  return parsed;
}

/**
 * 用解析出的层次名称替换某一侧当事人。
 * @param parties 当事人
 * @param role 角色
 * @param parsed 层次名称
 * @param idPrefix 新行编号前缀
 */
export function replacePartyRoleLayers(
  parties: LitigationParty[],
  role: LitigationPartyRole,
  parsed: Array<{layer: LitigationPartyLayer; name: string}>,
  idPrefix: string,
): LitigationParty[] {
  const others = parties.filter((item) => item.role !== role);
  const next = parsed.filter((row) => row.name.trim()).map((row) => ({
    id: `${idPrefix}_${row.layer}`,
    role,
    name: row.name.trim().slice(0, 200),
    contact: '',
    layer: row.layer,
  }));
  return [...others, ...next];
}

/**
 * 写入某一角色的名称。空名称会清掉已有的那一行。
 * @param parties 当事人
 * @param role 角色
 * @param name 名称
 * @param id 新行编号
 */
export function upsertPartyRoleName(parties: LitigationParty[], role: LitigationPartyRole, name: string, id: string): LitigationParty[] {
  const text = name.trim().slice(0, 200);
  const next = parties.map((item) => ({...item}));
  const existing = next.find((item) => item.role === role);
  if (existing) {
    existing.name = text;
    return next;
  }
  if (!text) return next;
  next.push({id, role, name: text, contact: ''});
  return next;
}

/**
 * 列表展示用的我方名称。
 * @param item 案件
 */
export function ourPartyName(item: Pick<LitigationCase, 'ourEntity' | 'parties'>): string {
  const named = item.parties.find((party) => party.role === 'our' && party.name.trim())?.name.trim();
  return named || item.ourEntity.trim();
}

/**
 * 所选阶段不在旧节点里时，按当前案件类型的程序模板补齐，并保留同名节点已填的日期。
 * 执行专有阶段会改用执行程序节点。
 * @param nodes 现有节点
 * @param disputeType 争议类型
 * @param stageName 目标阶段
 */
export function alignProcedureNodesForStage(
  nodes: LitigationProcedureNode[],
  disputeType: LitigationDisputeType,
  stageName: string,
): LitigationProcedureNode[] {
  const name = stageName.trim();
  if (!name || name === '已结案' || nodes.some((node) => node.name === name)) return nodes;
  const template = ENFORCEMENT_ONLY_STAGE_NAMES.has(name)
    ? ENFORCEMENT_TEMPLATE.map((item) => ({...item, plannedDate: '', completedDate: '', note: ''}))
    : procedureTemplateFor(disputeType);
  if (!template.some((node) => node.name === name)) return nodes;
  const previous = new Map(nodes.map((node) => [node.name, node]));
  return template.map((node) => {
    const old = previous.get(node.name);
    return old ? {...node, plannedDate: old.plannedDate, completedDate: old.completedDate, note: old.note} : node;
  });
}

/**
 * 将列表阶段选择同步到节点完成状态。
 * @param nodes 节点
 * @param stageName 目标阶段名
 * @param completedDate 完成日
 */
export function applyStageToNodes(
  nodes: LitigationProcedureNode[],
  stageName: string,
  completedDate: string,
): LitigationProcedureNode[] {
  const index = nodes.findIndex((node) => node.name === stageName);
  if (index < 0) return nodes;
  return nodes.map((node, nodeIndex) => {
    if (nodeIndex < index) {
      return {...node, completedDate: node.completedDate || completedDate};
    }
    if (nodeIndex === index) {
      return {...node, completedDate: ''};
    }
    return {...node, completedDate: ''};
  });
}
